/**
 * Composable for making screen reader announcements via a live region pattern.
 *
 * Dispatches CustomEvents that an AriaAnnouncer component picks up, or can be
 * used standalone with the reactive message ref.
 *
 * @returns announce function and reactive message ref
 *
 * @example
 * const { announce } = useAriaAnnounce()
 * announce('Video loaded and ready to play')
 */

import { ref } from 'vue'

export interface AnnounceEvent {
  message: string
  priority: 'polite' | 'assertive'
}

const currentMessage = ref('')

export function useAriaAnnounce() {
  const announce = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    currentMessage.value = message

    const event = new CustomEvent<AnnounceEvent>('aria-announce', {
      detail: {
        message,
        priority,
      },
    })
    window.dispatchEvent(event)
  }

  const announcePolite = (message: string) => announce(message, 'polite')
  const announceAssertive = (message: string) => announce(message, 'assertive')

  return {
    announce,
    announcePolite,
    announceAssertive,
    currentMessage,
  }
}

/**
 * Announce that a video has loaded and is ready to play.
 */
export function announceVideoLoaded() {
  const { announcePolite } = useAriaAnnounce()
  announcePolite('Video loaded and ready to play')
}

/**
 * Announce buffering state changes.
 *
 * @param isBuffering - Whether the video is currently buffering
 */
export function announceBuffering(isBuffering: boolean) {
  const { announcePolite } = useAriaAnnounce()
  announcePolite(isBuffering ? 'Buffering video' : 'Video playback resumed')
}

/**
 * Announce a seek position change.
 *
 * @param time - The new time position in seconds
 * @param formattedTime - Human-readable time string
 */
export function announceSeekChange(time: number, formattedTime: string) {
  const { announcePolite } = useAriaAnnounce()
  announcePolite(`Seeking to ${formattedTime}`)
}

/**
 * Announce that the video has ended.
 */
export function announceVideoEnded() {
  const { announcePolite } = useAriaAnnounce()
  announcePolite('Video playback ended')
}

/**
 * Announce a player error.
 *
 * @param errorMessage - Description of the error
 */
export function announceError(errorMessage: string) {
  const { announceAssertive } = useAriaAnnounce()
  announceAssertive(`Error: ${errorMessage}`)
}

/**
 * Announce volume changes.
 *
 * @param volumePercent - Volume level as a percentage (0-100)
 * @param isMuted - Whether the player is muted
 */
export function announceVolumeChange(volumePercent: number, isMuted: boolean) {
  const { announcePolite } = useAriaAnnounce()
  if (isMuted) {
    announcePolite('Volume muted')
  } else {
    announcePolite(`Volume ${Math.round(volumePercent)}%`)
  }
}

/**
 * Announce playback speed changes.
 *
 * @param speed - The new playback speed multiplier
 */
export function announcePlaybackSpeedChange(speed: number) {
  const { announcePolite } = useAriaAnnounce()
  announcePolite(`Playback speed ${speed}x`)
}

/**
 * Announce quality level changes.
 *
 * @param quality - Human-readable quality label (e.g. "720p")
 */
export function announceQualityChange(quality: string) {
  const { announcePolite } = useAriaAnnounce()
  announcePolite(`Video quality changed to ${quality}`)
}

/**
 * Announce loading state changes.
 *
 * @param isLoading - Whether the video is currently loading
 */
export function announceLoading(isLoading: boolean) {
  const { announcePolite } = useAriaAnnounce()
  announcePolite(isLoading ? 'Loading video' : 'Video loaded')
}
